/** How a committed condition reads on its chip and in the chip editor. Display only:
 *  the DSL text (what `?q=` carries) is unchanged and shows in the chip's tooltip. */
import { isLiveWindow, parseBound, windowLabel } from '@/components/TimeWindowPicker';

import type { ChipToken } from './chips';
import type { NamespaceConfig } from './types';

export type CondChip = Extract<ChipToken, { t: 'cond' }>;

export interface CondDisplay {
  /** The dimmed operator mark between namespace and value (`=`, `=~`, `#`); null when
   *  the value reads as words ("last 15 minutes"). */
  sigil: string | null;
  text: string;
  /** A relative time window: moves with every refresh. */
  live: boolean;
}

/** Display a value with human-friendly quoting (only quote if it has spaces). */
export function displayValue(value: string): string {
  return /\s/.test(value) ? `"${value}"` : value;
}

function timeWindowText(chip: CondChip): CondDisplay | null {
  const v = chip.value;
  let raw: [string | null, string | null];
  if (chip.operator === 'range' && Array.isArray(v)) raw = [v[0], v[1]];
  else if (chip.operator === 'gt' && !Array.isArray(v)) raw = [v, null];
  else if (chip.operator === 'lt' && !Array.isArray(v)) raw = [null, v];
  else return null;
  const from = raw[0] === null ? null : parseBound(raw[0]);
  const to = raw[1] === null ? null : parseBound(raw[1], { end: true });
  // A bound typed in a shape the panel can't read keeps its literal text.
  if ((raw[0] !== null && from === null) || (raw[1] !== null && to === null)) return null;
  const text = windowLabel(from, to, { tz: 'utc', zone: true });
  return text === null ? null : { sigil: null, text, live: isLiveWindow(from, to) };
}

export function condDisplay(chip: CondChip, cfg: NamespaceConfig | undefined): CondDisplay {
  if (cfg?.valueType === 'datetime') {
    const window = timeWindowText(chip);
    if (window) return window;
  }
  const sigil = chip.operator === 'icontains' ? '=~' : chip.operator === 'idmatch' ? '#' : '=';
  const v = chip.value;
  let text: string;
  if (chip.operator === 'range' && Array.isArray(v))
    text = `${displayValue(v[0])}..${displayValue(v[1])}`;
  else {
    const scalar = displayValue(Array.isArray(v) ? v[0] : v);
    text = chip.operator === 'gt' ? `${scalar}..` : chip.operator === 'lt' ? `..${scalar}` : scalar;
  }
  return { sigil, text, live: false };
}
